<template>
  <div ref="wrapper" :class="['ex-drawer--wrapper', { open: visible }]">
    <div class="ex-drawer--overlay"  @click="closeCalculator"></div>
    <div v-if="showTrigger" class="ex-drawer--trigger" @click="openCalculator">
      <p>{{title}}</p>
    </div>
    <div class="ex-drawer">
      <div class="ex-drawer--header">
        <p>{{title}}</p>
        <div @click="closeCalculator">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity="0.5">
              <path d="M10 19C14.9706 19 19 14.9706 19 10C19 5.02944 14.9706 1 10 1C5.02944 1 1 5.02944 1 10C1 14.9706 5.02944 19 10 19Z" stroke="#77A964" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.6988 7.2998L7.29883 12.6998" stroke="#77A964" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7.29883 7.2998L12.6988 12.6998" stroke="#77A964" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
          </svg>
        </div>
      </div>

      <div class="ex-drawer--body">
        <div>
          <div class="md-size-100">
            <span v-if="showBreakdown && directAssessment.assessmentYear && !inputGiven" class="danger intro">* All inputs are empty</span>
            <span v-if="showBreakdown && !directAssessment.assessmentYear" class="danger intro">* Please select the year</span>
            <v-select
              v-model="directAssessment.assessmentYear"
              placeholder="Select Year *"
              :options="assessmentYears">
            </v-select>
          </div>
        </div>

        <div class="mt-4" v-show="sectionForm === ''">
          <p class="intro">Below are the sections you need to fill to get an accurate value of your amount due for tax.</p>

          <div class="disclaimer-wrapper">
            <p class="dis-title">Disclaimer</p>

            <div class="intro disclaimer-note">
              <div style="text-align: justify!important;">
                <span>This computation is in line with the current finance act law and the tax calculator is intended to be an illustrative and educational tool only.<span v-if="!seeMore">..</span></span>

                <span v-if="seeMore"> It provides only a rough approximation of your taxes, it is subject to variations, it does not constitute tax advice. Please contact a tax professional if necessary.</span>

                <span v-if="!seeMore" @click="toggleSeeMore" class="pull-right see-more-lnk">See More</span>
                <span v-if="seeMore" @click="toggleSeeMore" class="pull-right see-more-lnk">See Less</span>
              </div>
            </div>
          </div>

          <div
            v-for="(value, key, index) in sections"
            :key="index"
            :class="[' mt-4 ex-drawer-section', { active: validateInputs[key] }]"
            @click="sectionForm = key">
            <div class="label">
              <p>{{ value.label }}</p>
              <span>{{ value.description }}</span>
            </div>
            <div class="value">
              <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 7H15.1429M10 1L16 7L10 13" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <div v-if="sectionForm === 'income'">
          <div class="section-heading">
            <p>{{ sections[sectionForm].label }}</p>
            <span>{{ sections[sectionForm].description }}</span>
          </div>
          <div class="section-tab">
            <div :class="{ active: sections.income.tab === 'earned' }" @click="sections.income.tab = 'earned'"><span>Earned</span></div>
            <div :class="{ active: sections.income.tab === 'unearned' }" @click="sections.income.tab = 'unearned'"><span>Unearned</span></div>
            <div v-if="isSectionActive('capitalGainsInput')" :class="{ active: sections.income.tab === 'capital_gains' }" @click="sections.income.tab = 'capital_gains'"><span>Capital Gains</span></div>
          </div>
          <div class="section-fields">
            <div v-show="sections.income.tab === 'earned'">
              <div class="fields">
                <template v-for="field in filteredIncomeInput">
                  <md-field v-bind:key="field.code">
                    <label>{{ field.placeholder }}</label>
                    <md-input
                      v-model="field.value"
                      v-currency="currencyOptions"
                      class="mb-3"
                      :disabled="field.disabled"
                      :placeholder="field.placeholder"
                    />
                    <!-- Add button only for Other Income -->
                    <md-button
                      v-if="field.code === 'other_incomes'"
                      @click="addOtherIncome(incomeInput.otherIncome)"
                      class="md-just-icon md-simple md-success"
                    >
                      <md-icon>add</md-icon>
                    </md-button>
                  </md-field>

                  <!-- Render dynamic rows RIGHT AFTER other income -->
                  <div
                    v-bind:key="`other-income-${field.code}`"
                    v-if="field.code === 'other_incomes'"
                    class="md-layout md-size-100"
                  >
                    <div
                      class="md-layout md-size-100"
                      v-for="(item, index) in otherIncomes.filter(
                        oInc => oInc.item_type_id === incomeInput.otherIncome.id
                      )"
                      :key="index"
                    >
                      <div class="md-layout-item md-size-40">
                        <md-field>
                          <label>Name</label>
                          <md-input v-model.trim="item.name"></md-input>
                        </md-field>
                      </div>

                      <div class="md-layout-item md-size-40">
                        <md-field>
                          <label>Amount</label>
                          <md-input
                            v-model.trim="item.amount"
                            @input="calculateTotalOtherIncome(incomeInput.otherIncome)"
                            v-currency="currencyOptions"
                          />
                        </md-field>
                      </div>

                      <md-button
                        class="md-just-icon md-simple md-danger"
                        title="Remove"
                        @click="removeOtherIncome(item, incomeInput.otherIncome)"
                      >
                        <md-icon>close</md-icon>
                      </md-button>
                    </div>
                  </div>
                </template>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sections.income.tab = 'unearned'">Next</md-button>
              </div>
            </div>
            <div v-show="sections.income.tab === 'unearned'">
              <div class="fields">
                <md-field v-for="field in filteredUntaxableIncomeInput" :key="field.code">
                  <label>{{ field.placeholder }}</label>
                  <md-input
                    v-model="field.value"
                    v-currency="currencyOptions"
                    class="mb-3"
                    :disabled="field.disabled"
                    :placeholder="field.placeholder"
                  />
                </md-field>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="nextTab('capitalGainsInput', 'income', 'capital_gains')">Next</md-button>
              </div>
            </div>
            <div v-show="sections.income.tab === 'capital_gains'" v-if="isSectionActive('capitalGainsInput')">
              <div class="fields">
                <md-field v-for="field in filteredCapitalGainsInput" :key="field.code">
                  <label>{{ field.placeholder }}</label>
                  <md-input
                    v-model="field.value"
                    v-currency="currencyOptions"
                    class="mb-3"
                    :disabled="field.disabled"
                    :placeholder="field.placeholder"
                  />
                </md-field>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sectionForm = ''">Next</md-button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="sectionForm === 'deductions'">
          <div class="section-heading">
            <p>{{ sections[sectionForm].label }}</p>
            <span>{{ sections[sectionForm].description }}</span>
          </div>
          <div class="section-tab">
            <div :class="{ active: sections.deductions.tab === 'fll' }" @click="sections.deductions.tab = 'fll'"><span>Fll Income</span></div>
            <div :class="{ active: sections.deductions.tab === 'other-deductions' }" @click="sections.deductions.tab = 'other-deductions'"><span>Other deductions</span></div>
          </div>
          <div class="section-fields">
            <div v-show="sections.deductions.tab === 'fll'">
              <div class="fields">
                <md-field v-for="field in filteredFiiIncomeInput" :key="field.code">
                  <label>{{ field.placeholder }}</label>
                  <md-input
                    v-model="field.value"
                    v-currency="currencyOptions"
                    class="mb-3"
                    :disabled="field.disabled"
                    :placeholder="field.placeholder"
                  />
                </md-field>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sections.deductions.tab = 'other-deductions'">Next</md-button>
              </div>
            </div>
            <div v-show="sections.deductions.tab === 'other-deductions'">
              <div class="fields">
                <md-field v-for="field in filteredDeductionsInput" :key="field.code">
                  <label>{{ field.placeholder }}</label>
                  <md-input
                    v-model="field.value"
                    v-currency="currencyOptions"
                    class="mb-3"
                    :disabled="field.disabled"
                    :placeholder="field.placeholder"
                  />
                </md-field>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sectionForm = ''">Next</md-button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="sectionForm === 'adjustments'">
          <div class="section-heading">
            <p>{{ sections[sectionForm].label }}</p>
            <span>{{ sections[sectionForm].description }}</span>
          </div>
          <div class="section-fields">
            <div class="fields">
              <md-field v-for="field in filteredAdjustmentsInput" :key="field.code">
                <label>{{ field.placeholder }}</label>
                <md-input
                  v-model="field.value"
                  v-currency="currencyOptions"
                  class="mb-3"
                  :disabled="field.disabled"
                  :placeholder="field.placeholder"
                />
              </md-field>
            </div>
            <div class="button">
              <md-button class="md-success md-dense" @click="sectionForm = ''">Next</md-button>
            </div>
          </div>
        </div>

        <div v-if="sectionForm === 'reliefs'">
          <div class="section-heading">
            <p>{{ sections[sectionForm].label }}</p>
            <span>{{ sections[sectionForm].description }}</span>
          </div>
          <div class="section-tab">
            <div :class="{ active: sections.reliefs.tab === 'personal' }" @click="sections.reliefs.tab = 'personal'"><span>Personal</span></div>
            <div :class="{ active: sections.reliefs.tab === 'investment' }" @click="sections.reliefs.tab = 'investment'"><span>Investment</span></div>
          </div>
          <div class="section-fields">
            <div v-show="sections.reliefs.tab === 'personal'">
              <div class="fields">
                <template v-for="field in filteredReliefsInput">
                  <md-field v-bind:key="field.code" v-if="field.code !== 'ordinary_share_holding'">
                    <label>{{ field.placeholder }}</label>
                    <md-input
                      v-model="field.value"
                      v-currency="currencyOptions"
                      class="mb-3"
                      :disabled="field.disabled"
                      :placeholder="field.placeholder"
                    />
                    <!-- Add button only for Other Income -->
                    <md-button
                      v-if="field.code === 'others'"
                      @click="addOtherIncome(reliefsInput.others)"
                      class="md-just-icon md-simple md-success"
                    >
                      <md-icon>add</md-icon>
                    </md-button>
                  </md-field>

                  <!-- Render dynamic rows RIGHT AFTER other income -->
                  <div
                    v-bind:key="`other-income-relief-${field.code}`"
                    v-if="field.code === 'others'"
                    class="md-layout md-size-100"
                  >
                    <div
                      class="md-layout md-size-100"
                      v-for="(item, index) in otherIncomes.filter(
                        oInc => oInc.item_type_id === reliefsInput.others.id
                      )"
                      :key="index"
                    >
                      <div class="md-layout-item md-size-40">
                        <md-field>
                          <label>Name</label>
                          <md-input v-model.trim="item.name"></md-input>
                        </md-field>
                      </div>

                      <div class="md-layout-item md-size-40">
                        <md-field>
                          <label>Amount</label>
                          <md-input
                            v-model.trim="item.amount"
                            @input="calculateTotalOtherIncome(reliefsInput.others)"
                            v-currency="currencyOptions"
                          />
                        </md-field>
                      </div>

                      <md-button
                        class="md-just-icon md-simple md-danger"
                        title="Remove"
                        @click="removeOtherIncome(item, reliefsInput.others)"
                      >
                        <md-icon>close</md-icon>
                      </md-button>
                    </div>
                  </div>
                </template>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sections.reliefs.tab = 'investment'">Next</md-button>
              </div>
            </div>
            <div v-show="sections.reliefs.tab === 'investment'">
              <div class="fields">
                <template v-for="field in filteredReliefsInput">
                  <md-field v-bind:key="field.code" v-if="field.code === 'ordinary_share_holding'">
                    <label>{{ field.placeholder }}</label>
                    <md-input
                      v-model="field.value"
                      v-currency="currencyOptions"
                      class="mb-3"
                      :disabled="field.disabled"
                      :placeholder="field.placeholder"
                    />
                  </md-field>
                </template>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sectionForm = ''">Next</md-button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="sectionForm === 'wht'">
          <div class="section-heading">
            <p>{{ sections[sectionForm].label }}</p>
            <span>{{ sections[sectionForm].description }}</span>
          </div>
          <div class="section-fields">
            <div class="fields">
              <md-field v-for="field in filteredWithheldTaxesInput" :key="field.code">
                <label>{{ field.placeholder }}</label>
                <md-input
                  v-model="field.value"
                  v-currency="currencyOptions"
                  class="mb-3"
                  :disabled="field.disabled"
                  :placeholder="field.placeholder"
                />
              </md-field>
            </div>
            <div class="button">
              <md-button class="md-success md-dense" @click="sectionForm = ''">Next</md-button>
            </div>
          </div>
        </div>

        <div v-if="sectionForm === 'others'">
          <div class="section-heading">
            <p>{{ sections[sectionForm].label }}</p>
            <span>{{ sections[sectionForm].description }}</span>
          </div>
          <div class="section-tab">
            <div :class="{ active: sections.others.tab === 'prepayments' }" @click="sections.others.tab = 'prepayments'"><span>Prepayments</span></div>
            <div :class="{ active: sections.others.tab === 'direct' }" @click="sections.others.tab = 'direct'"><span>Direct Remittances</span></div>
          </div>
          <div class="section-fields">
            <div v-show="sections.others.tab === 'prepayments'">
              <div class="fields">
                <template v-for="field in filteredOthersPrepaymentsInput">
                  <md-field v-bind:key="field.code" v-if="!['payment_on_acccount', 'others_payments'].includes(field.code)">
                    <label>{{ field.placeholder }}</label>
                    <md-input
                      v-model="field.value"
                      v-currency="currencyOptions"
                      class="mb-3"
                      :disabled="field.disabled"
                      :placeholder="field.placeholder"
                    />
                  </md-field>
                </template>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sections.others.tab = 'direct'">Next</md-button>
              </div>
            </div>
            <div v-show="sections.others.tab === 'direct'">
              <div class="fields">
                <template v-for="field in filteredOthersPrepaymentsInput">
                  <md-field v-bind:key="field.code" v-if="['payment_on_acccount', 'others_payments'].includes(field.code)">
                    <label>{{ field.placeholder }}</label>
                    <md-input
                      v-model="field.value"
                      v-currency="currencyOptions"
                      class="mb-3"
                      :disabled="field.disabled"
                      :placeholder="field.placeholder"
                    />
                  </md-field>
                </template>
              </div>
              <div class="button">
                <md-button class="md-success md-dense" @click="sectionForm = ''">Next</md-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div :class="['ex-drawer--footer', { open: showBreakdown }]">
        <div class="summary">
          <div>
            <h5>TAX DUE</h5>
            <span v-if="!showBreakdown" @click="callCalculate">See breakdown</span>
            <span v-if="showBreakdown" @click="showBreakdown=false">Close breakdown</span>
          </div>
          <h4>&#8358;{{$numeral(directAssessment.taxDue).format('0,0.00')}}</h4>
        </div>
        <div v-if="showBreakdown" class="breakdown">
          <div v-if="directAssessment.statutoryTotal">
            <p>Statutory Income</p>
            <span>&#8358;{{$numeral(directAssessment.statutoryTotal).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.netAdjustment">
            <p>Net Adjustment</p>
            <span>&#8358;{{$numeral(directAssessment.netAdjustment).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.totalRelief">
            <p>Total Relief</p>
            <span>&#8358;{{$numeral(directAssessment.totalRelief).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.withholdingTax">
            <p>Withholding Tax</p>
            <span>&#8358;{{$numeral(directAssessment.withholdingTax).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.deductions">
            <p>Deductions</p>
            <span>&#8358;{{$numeral(directAssessment.deductions).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.totalIncome">
            <p>Total Income</p>
            <span>&#8358;{{$numeral(directAssessment.totalIncome).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.chargeableIncome">
            <p>Chargeable Income</p>
            <span>&#8358;{{$numeral(directAssessment.chargeableIncome).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.netPayable">
            <p>Net Payable</p>
            <span>&#8358;{{$numeral(directAssessment.netPayable).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.totalAssessableIncome">
            <p>Total Ass. Income</p>
            <span>&#8358;{{$numeral(directAssessment.totalAssessableIncome).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.cra">
            <p>Consolidated Relief</p>
            <span>&#8358;{{$numeral(directAssessment.cra).format('0,0.00')}}</span>
          </div>
          <div v-if="directAssessment.taxCharged">
            <p>Tax Charged</p>
            <span>&#8358;{{$numeral(directAssessment.taxCharged).format('0,0.00')}}</span>
          </div>
          <span class="close" @click="showBreakdown = false">Close breakdown</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { NavTabsCard, Tabs } from "@/components";
import { convertToNumber, fetchYearsFromBaseYear } from "@/lib/utils";
import { CurrencyDirective } from 'vue-currency-input';
import { resolveTaxRules, taxLawByYear } from "@/lib/taxRules";

const directAssessment = {
  statutoryTotal: "",
  deductions: "",
  totalAssessableIncome: "",
  netAdjustment: "",
  totalIncome: "",
  cra: "",
  totalRelief: "",
  chargeableIncome: "",
  taxCharged: "",
  withholdingTax: "",
  netPayable: "",
  taxDue: ""
};

export default {
  name: 'TaxCalculatorNew',

  props: {
    redirectBackPath: Object,
    showTrigger: {
      type: Boolean,
      default: true
    },
    income: {
      type: String,
      default: ""
    }
  },

  components: {
    NavTabsCard,
    Tabs
  },

  directives: {
    currency: CurrencyDirective
  },

  data() {
    return {
      title: 'Tax Calculator',
      seeMore: false,
      visible: false,
      sectionForm: "",
      sections: {
        income: {
          label: 'Income',
          description: 'This covers for incomes.',
          amountKey: 'chargeableIncome',
          tab: 'earned',
          input: ['incomeInput', 'untaxableIncomeInput', 'capitalGainsInput']
        },

        deductions: {
          label: 'Deductions',
          description: 'This covers for all deductions.',
          amountKey: 'deductions',
          tab: 'fll',
          input: ['deductionsInput']
        },

        adjustments: {
          label: 'Adjustments',
          description: 'This covers for all adjustments.',
          amountKey: 'netAdjustment',
          input: ['adjustmentsInput']
        },

        reliefs: {
          label: 'Reliefs',
          description: 'This covers for all reliefs.',
          amountKey: 'totalRelief',
          tab: 'personal',
          input: ['reliefsInput']
        },

        wht: {
          label: 'Withheld Taxes',
          description: 'This covers for all wht.',
          amountKey: 'withholdingTax',
          input: ['withheldTaxesInput']
        },

        others: {
          label: 'Others',
          description: 'This covers for all other payments.',
          amountKey: '',
          tab: 'prepayments',
          inputs: ['othersPrepaymentsInput', 'directRemittanceInput']
        }
      },
      showBreakdown: false,
      currencyOptions: {
        currency: {prefix: ''}
      },

      otherIncomes: [],

      incomeInput: {
        tradeBusiness: {code: 'trade_business', value: '', placeholder: ' Trade Business or profession'},
        employment: {code: 'employment', value: '', placeholder: 'Employment'},
        consultancyFee: {code: 'consultancy', value: '', placeholder: 'Consultancy/Professional Fees'},
        otherIncome: {code: 'other_incomes', value: '', id: 'other_income', placeholder: 'Other Incomes', disabled: true},
        directorFee: {code: 'director_fees', value: '', placeholder: 'Director Fees'},
        benefitInKind: {code: 'b_i_k', value: '', placeholder: 'Benefit in Kind(BIK)'},
        terminalBonus: {code: 'terminal_bonus', value: '', placeholder: 'Terminal Bonus'},
        contract: {code: 'contract', value: '', placeholder: 'Contract'},
        pension: {code: 'pension', value: '', placeholder: 'Pension'},
        allowances: {code: 'allowances', value: '', placeholder: 'Allowances'},
        profitShare: {code: 'profit_shares', value: '', placeholder: 'Profit Shares'},
        commission: {code: 'commission', value: '', placeholder: 'Commission'},
        gamesAndWinnings: {code: 'games_and_winnings', value: '', placeholder: 'Games and Winnings'},
        estateIncome: {code: 'estate_income', value: '', placeholder: 'Estate Income'},
        gratuity: {code: 'gratuity', value: '', placeholder: 'Gratuity'},
        earnedBonus: {code: 'earned_bonus', value: '', placeholder: 'Bonus'},
        compensationForLossOfEmployment: {code: 'loss_employment_compensation', value: '', placeholder: 'Compensation for Loss of Employment'},
        honoraria: {code: 'honoraria', value: '', placeholder: 'Honoraria'},
        prizesAndAwards: {code: 'prize', value: '', placeholder: 'Prizes and Awards'},
        grants: {code: 'grant', value: '', placeholder: 'Grants'},
      },
      untaxableIncomeInput: {
        dividend: {code: 'dividend', value: '', placeholder: 'Dividend'},
        interest: {code: 'interest', value: '', placeholder: 'Interest'},
        annuities: {code: 'annuities', value: '', placeholder: 'Annuities'},
        royalty: {code: 'royalty', value: '', placeholder: 'Royalty'},
        rent: {code: 'rent', value: '', placeholder: 'Rent'},
        discounts: {code: 'discounts', value: '', placeholder: 'Discounts'},
        incomeFromSecurities: {code: 'income_from_securities', value: '', placeholder: 'Income from Securities'},
        familyIncome: {code: 'family_income', value: '', placeholder: 'Family Income'},
      },
      capitalGainsInput: {
        disposalOfMoneyInstruments: {code: 'disposal_money_instruments', value: '', placeholder: 'Disposal of Money Instruments'},
        digitalAssets: {code: 'digital_assets', value: '', placeholder: 'Digital Assets'},
        otherCapitalGains: {code: 'other_cgd', value: '', placeholder: 'Chargeable Gains Deduction'}
      },
      fiiIncome: {
        dividendAmt: {code: 'fii_dividend', value: '', placeholder: 'Dividend (FII)', disabled: true},
        interestAmt: {code: 'fii_interest', value: '', placeholder: 'Interest (FII)', disabled: true},
        annuitiesAmt: {code: 'fii_annuities', value: '', placeholder: 'Annuities (FII)', disabled: true},
      },
      deductionsInput: {
        pensions: {code: 'pensions', value: '', placeholder: 'Pensions'},
        gratuities: {code: 'gratuities', value: '', placeholder: 'Gratuities'},
        bonus: {code: 'other_deduction_bonus', value: '', placeholder: 'Bonus'},
        otherDeductions: {code: 'other_deductions', value: '', placeholder: 'Other Deductions'},
        incomeFromSecuritiesDeduct: {code: 'income_from_securities_deduct', value: '', placeholder: 'Income from Securities', disabled: true},
        compensationForLossOfEmploymentDeduct: {code: 'loss_employment_compensation_deduct', value: '', placeholder: 'Compensation for Loss of Employment', disabled: true},
      },
      adjustmentsInput: {
        balancingCharge: {code: 'balancing_charge', value: '', placeholder: 'Balancing Charge'},
        balancingAllowance: {code: 'balancing_allowance', value: '', placeholder: 'Balancing Allowance'},
        losses: {code: 'losses', value: '', placeholder: 'Losses'},
        capitalAllowance: {code: 'capital_allowance', value: '', placeholder: 'Capital Allowance'}
      },
      reliefsInput: {
        cra: {code: 'consolidated_relief_allowance', value: '', placeholder: 'Consolidated Relief Allowance'},
        pra: {code: 'personal_relief_allowance', value: '', placeholder: 'Personal Relief Allowance'},
        personalAllowance: {code: 'personal_allowance', value: '', placeholder: 'Personal Allowance'},
        nationalHealthInsuranceScheme: {code: 'nhis', value: '', placeholder: 'National Health Insurance Scheme'},
        nationalHousingFund: {code: 'nhf', value: '', placeholder: 'National Housing Fund'},
        childrenAllowance: {code: 'children_allowance', value: '', placeholder: 'Children Allowance'},
        dependentRelatives: {code: 'dependent_relatives', value: '', placeholder: 'Dependent Relatives'},
        lifeAssurance: {code: 'life_assurance', value: '', placeholder: 'Life Assurance'},
        nationalPensionScheme: {code: 'nps', value: '', placeholder: 'National Pension Scheme'},
        rentRelief: {code: 'rent_paid', value: '', placeholder: 'Annual Rent Paid'},
        others: {code: 'others', value: '', id: 'other_reliefs', placeholder: 'Others', disabled: true},
        ordinaryShareHolding: {code: 'ordinary_share_holding', value: '', placeholder: 'Ordinary Share Holding'}
      },
      withheldTaxesInput: {
        whtOnRent: {code: 'wht_rent', value: '', placeholder: 'WHT on Rent'},
        whtOnCommission: {code: 'wht_commission', value: '', placeholder: 'WHT on Commission'},
        whtOnConsultancyService: {code: 'wht_consultancy', value: '', placeholder: 'WHT on Consultancy/Professional Service'},
        whtOnDirectorsFee: {code: 'wht_directors', value: '', placeholder: 'WHT on Directors Fee'},
        whtOnContract: {code: 'wht_contract', value: '', placeholder: 'WHT on Contract'},
        whtOnInterest: {code: 'wht_interest', value: '', placeholder: 'WHT on Interest'},
        whtOthers: {code: 'wht_others', value: '', placeholder: 'WHT Others'}
      },
      othersPrepaymentsInput: {
        doubleTaxationReliefs: {code: 'double_taxation_reliefs', value: '', placeholder: 'Double Taxation Reliefs'},
        commonWealthIncomeTax: {code: 'common_wealth_income_tax', value: '', placeholder: 'Common Wealth Income Tax'},
        overPayments: {code: 'over_payments', value: '', placeholder: 'Over Payments'},
        paye: {code: 'paye', value: '', placeholder: 'PAYE'},
        prepaymentBonus: {code: 'prepayment_bonus', value: '', placeholder: 'Bonus'},
        paymentOnAccount: {code: 'payment_on_acccount', value: '', placeholder: 'Payment on Account'}
      },
      directAssessment: {
        assessmentYear: "",
        ...directAssessment
      },
      nonTaxableItems: ['pension', 'dividend', 'interest', 'royalty', 'rent'],
      calculatorHash: '#tax_calculator',
      assessmentYears: fetchYearsFromBaseYear(this.$constants.baseYear, true),
      inputGiven: false,
      applicableByLaw: true
    };
  },

  computed: {
    assessmentYearOfRevision() {
      return parseInt(this.directAssessment.assessmentYear);
    },

    taxLawRevisionYearBase() {
      return this.assessmentYearOfRevision <= taxLawByYear['revision_base'];
    },

    taxLawRevisionYear2026() {
      return this.assessmentYearOfRevision >= taxLawByYear['revision_2026'];
    },

    activeTaxRules() {
      return resolveTaxRules(this.directAssessment.assessmentYear);
    },

    filteredIncomeInput() {
      return this.filterFields(
        this.incomeInput,
        this.activeTaxRules.incomeInput
      );
    },

    filteredUntaxableIncomeInput() {
      return this.filterFields(
        this.untaxableIncomeInput,
        this.activeTaxRules.untaxableIncomeInput
      );
    },

    filteredCapitalGainsInput() {
      if (
        this.activeTaxRules.capitalGainsInput &&
        this.activeTaxRules.capitalGainsInput._section === false
      ) {
        return {};
      }

      return this.filterFields(
        this.capitalGainsInput,
        this.activeTaxRules.capitalGainsInput
      );
    },

    filteredFiiIncomeInput() {
      return this.filterFields(
        this.fiiIncome,
        this.activeTaxRules.fiiIncome
      );
    },

    filteredDeductionsInput() {
      return this.filterFields(
        this.deductionsInputsBySelectedYear,
        this.activeTaxRules.deductionsInput
      );
    },

    filteredAdjustmentsInput() {
      return this.filterFields(
        this.adjustmentsInput,
        this.activeTaxRules.adjustmentsInput
      );
    },

    filteredReliefsInput() {
      return this.filterFields(
        this.reliefsInput,
        this.activeTaxRules.reliefsInput
      );
    },

    filteredWithheldTaxesInput() {
      return this.filterFields(
        this.withheldTaxesInput,
        this.activeTaxRules.withheldTaxesInput
      );
    },

    filteredOthersPrepaymentsInput() {
      return this.filterFields(
        this.othersPrepaymentsInput,
        this.activeTaxRules.othersPrepaymentsInput
      );
    },

    activeFieldRules() {
      const rules = {};
      const active = this.activeTaxRules || {};

      Object.keys(active).forEach(section => {
        Object.assign(rules, active[section] || {});
      });

      return rules;
    },

    validateInputs() {
      const incomeInputValues = Object.keys(this.incomeInput).map(q => Object.values(this.incomeInput[q])[1]);
      const untaxableIncomeInputValues = Object.keys(this.untaxableIncomeInput).map(q => Object.values(this.untaxableIncomeInput[q])[1]);
      const capitalGainsInput = Object.keys(this.capitalGainsInput).map(q => Object.values(this.capitalGainsInput[q])[1]);
      const incomes = incomeInputValues.concat(untaxableIncomeInputValues).concat(capitalGainsInput);
      const deductionsInput = Object.keys(this.deductionsInput).map(q => Object.values(this.deductionsInput[q])[1]);
      const adjustmentsInput = Object.keys(this.adjustmentsInput).map(q => Object.values(this.adjustmentsInput[q])[1]);
      const reliefsInput = Object.keys(this.reliefsInput).map(q => Object.values(this.reliefsInput[q])[1]);
      const withheldTaxesInput = Object.keys(this.withheldTaxesInput).map(q => Object.values(this.withheldTaxesInput[q])[1]);
      const othersPrepaymentsInput = Object.keys(this.othersPrepaymentsInput).map(q => Object.values(this.othersPrepaymentsInput[q])[1]);

      return {
        income: Boolean(incomes.find(val => val)),
        deductions: Boolean(deductionsInput.find(val => val)),
        adjustments: Boolean(adjustmentsInput.find(val => val)),
        reliefs: Boolean(reliefsInput.find(val => val)),
        wht: Boolean(withheldTaxesInput.find(val => val)),
        others: Boolean(othersPrepaymentsInput.find(val => val)),
      }
    },

    deductionsInputsBySelectedYear() {
      const inputRules = { ...this.deductionsInput };
      inputRules.pensions.disabled = this.taxLawRevisionYear2026;
      inputRules.gratuities.disabled = this.taxLawRevisionYear2026;

      return inputRules;
    }
  },

  methods: {
    nextTab(section, parent, tab) {
      this.isSectionActive(section) ? this.sections[parent].tab = tab : this.sectionForm = '';
    },

    filterFields(sectionData, sectionRules) {
      if (!sectionRules) return sectionData;

      const filtered = {};

      Object.keys(sectionData).forEach(key => {
        if (sectionRules[key] !== false) {
          filtered[key] = sectionData[key];
        }
      });

      return filtered;
    },

    isSectionActive(section) {
      if (!this.activeTaxRules[section]) return true;

      return this.activeTaxRules[section]._section !== false;
    },

    isFieldActive(section, field) {
      if (!this.activeTaxRules[section]) return true;

      if (this.activeTaxRules[section][field] === undefined) return true;

      return this.activeTaxRules[section][field];
    },

    calculateTotalOtherIncome(otherIncome) {
      otherIncome.value = 0;
      this.otherIncomes.filter(oInc => oInc.item_type_id === otherIncome.id).forEach(income => otherIncome.value += convertToNumber(income.amount));
      otherIncome.value = this.$numeral(otherIncome.value).format('0,0.00');
    },

    addOtherIncome(inputItem) {
      this.otherIncomes.push({
        item_type_id: inputItem.id,
        name: '',
        amount: ''
      });
    },

    removeOtherIncome(item, otherIncome) {
      const incomePos = this.otherIncomes.indexOf(item);
      this.otherIncomes.splice(incomePos, 1);
      this.calculateTotalOtherIncome(otherIncome);
    },

    toggleSeeMore() {
      this.seeMore = !this.seeMore;
    },

    callCalculate() {
      this.showBreakdown = true;
      if(!this.directAssessment.assessmentYear) return;

      this.calculateTax();
    },

    openCalculator() {
      if(this.showTrigger) window.location.hash = this.calculatorHash;

      this.sectionForm = '';
      this.showBreakdown = false;
      this.visible = true;
      this.seeMore = false;
    },

    closeCalculator() {
      this.visible = false;
      this.$router.push(window.location.pathname);
      this.$emit("closed");
    },

    calculateTax() {
      if(!this.directAssessment.assessmentYear) return;

      const assessmentInput = {
        ...this.incomeInput,
        ...this.untaxableIncomeInput,
        ...this.capitalGainsInput,
        ...this.fiiIncome,
        ...this.deductionsInput,
        ...this.adjustmentsInput,
        ...this.reliefsInput,
        ...this.withheldTaxesInput,
        ...this.othersPrepaymentsInput
      };

      const inputByCode = {};
      Object.values(assessmentInput).forEach(item => {
        inputByCode[item.code] = item.value;
      });

      this.$http
        .post(`assessments/direct/structure?year=${this.directAssessment.assessmentYear}`, {
          method: "GET"
        })
        .then(
          response => {
            if (response.data) {
              const formParams = {};
              response.data.forEach(cf => {
                cf.groups.forEach(cg => {
                  const groupPayload = {};

                  cg.item_types.forEach(it => {
                    if (this.activeFieldRules[it.code] === false) return;

                    const value = inputByCode[it.code] || 0;
                    groupPayload[it.code] = convertToNumber(value);
                  });

                  if (Object.keys(groupPayload).length) {
                    formParams[cg.code] = groupPayload;
                  }
                });
              });

              const body = {
                year: this.directAssessment.assessmentYear,
                params: formParams
              };

              this.$http
                .post("assessments/direct/tax/calculation", {
                  body,
                  method: "POST"
                })
                .then(
                  response => {
                    const result = response.body;
                    this.directAssessment = {
                      assessmentYear: result.year,
                      statutoryTotal: result.total_statutory,
                      deductions: result.total_deduction,
                      totalAssessableIncome: result.total_assessable,
                      netAdjustment: result.net_adjustment,
                      totalIncome: result.total_income,
                      cra: result.consolidated_relief,
                      totalRelief: result.total_relief,
                      chargeableIncome: result.total_chargeable_income,
                      taxCharged: result.total_charged,
                      withholdingTax: result.total_wht,
                      netPayable: result.net_payable,
                      taxDue: result.total_outstanding
                    };

                    const { other_deductions } = result.calc_fields ? result.calc_fields : {};

                    if(other_deductions && this.taxLawRevisionYear2026) {
                      this.deductionsInput.incomeFromSecuritiesDeduct.value = other_deductions.income_from_securities_deduct || '';
                      this.deductionsInput.compensationForLossOfEmploymentDeduct.value = other_deductions.loss_employment_compensation_deduct || '';
                    }
                  },
                  error => {
                    this.notification(error.message, "clear", "danger");
                  }
                );

            } else {
              this.notification("An unknown error occurred", "clear", "danger");
            }
          },
          error => {
            this.notification(error.message, "clear", "danger");
          }
        );
    },

    resetForm() {
      Object.keys(this.incomeInput).forEach(key => this.incomeInput[key].value = '');
      Object.keys(this.untaxableIncomeInput).forEach(key => this.untaxableIncomeInput[key].value = '');
      Object.keys(this.capitalGainsInput).forEach(key => this.capitalGainsInput[key].value = '');
      Object.keys(this.fiiIncome).forEach(key => this.fiiIncome[key].value = '');
      Object.keys(this.deductionsInput).forEach(key => this.deductionsInput[key].value = '');
      Object.keys(this.adjustmentsInput).forEach(key => this.adjustmentsInput[key].value = '');
      Object.keys(this.reliefsInput).forEach(key => this.reliefsInput[key].value = '');
      Object.keys(this.withheldTaxesInput).forEach(key => this.withheldTaxesInput[key].value = '');
      Object.keys(this.othersPrepaymentsInput).forEach(key => this.othersPrepaymentsInput[key].value = '');
    },

    notification(message, icon, color) {
      this.$notify({
        message: message,
        icon: icon,
        horizontalAlign: "right",
        verticalAlign: "top",
        type: color
      });
    }
  },

  mounted() {
    this.visible = this.$route.hash === this.calculatorHash;
    this.seeMore = false;
  },

  watch: {
    'directAssessment.assessmentYear': function(val) {
      this.resetForm();

      this.directAssessment = {
        assessmentYear: val,
        ...directAssessment
      }

      if(val && this.showBreakdown) this.calculateTax();
    },

    'untaxableIncomeInput.dividend.value': function(val) {
      this.fiiIncome.dividendAmt.value = val;
    },

    'untaxableIncomeInput.interest.value': function(val) {
      this.fiiIncome.interestAmt.value = val;
    },

    'untaxableIncomeInput.annuities.value': function(val) {
      this.fiiIncome.annuitiesAmt.value = val;
    },

    'incomeInput.pension.value': function(val) {
      if(!this.taxLawRevisionYear2026) return;


      this.deductionsInput.pensions.value = val;
    },

    'incomeInput.gratuity.value': function(val) {
      if(!this.taxLawRevisionYear2026) return;

      this.deductionsInput.gratuities.value = val;
    },
  }
};
</script>

<style scoped lang="scss">
.ex-drawer--wrapper {
  overflow: hidden;
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  transition: all 0.3s ease-out;

  &.open {
    z-index: 9999999999;
    transition: all 0.3s ease-out;

    .ex-drawer--trigger {
      right: -100px;
      transition: right 0.3s ease-out;
    }

    .ex-drawer--overlay {
      opacity: 1;
      transition: all 0.3s ease-out;
    }

    .ex-drawer {
      right: 0;
      transition: all 0.3s ease-out;
    }
  }

  .ex-drawer--trigger {
    padding: 12px;
    position: absolute;
    right: -44px;
    top: 50%;
    transform: translateY(-50%) rotate(-90deg);
    cursor: pointer;
    background: #d95260;
    color: #fff;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    z-index: 112;
    opacity: 1;
    transition: right 0.6s ease-out;

    -webkit-animation: blink 1s linear infinite;
    -moz-animation: blink 1s linear infinite;
    -ms-animation: blink 1s linear infinite;
    -o-animation: blink 1s linear infinite;
    animation: blink 1s linear infinite;

    p {
      margin: 0;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1;
      text-transform: uppercase;
    }
  }

  .ex-drawer--overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    background: rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease-out;
  }

  .ex-drawer {
    width: 350px;
    height: 100%;
    background: #fff;
    position: absolute;
    right: -350px;
    z-index: 999999;
    top: 0;
    transition: all 0.3s ease-in;

    p, span {
      margin: 0;
    }

    &--header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px;

      p {
        font-weight: 500;
      }

      div {
        cursor: pointer;
      }
    }

    &--body {
      padding: 20px;
      height: 80vh;
      overflow-y: scroll;

      .intro {
        font-size: 12px;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .section-heading {
        margin-bottom: 20px;

        p {
          color: #4caf50;
        }
        span {
          font-size: 11px;
        }
      }

      .section-fields {
        .fields {
          height: 52vh;
          overflow-y: scroll;
          /* overflow-x: hidden;
          padding-left: 14px; */
        }
        .button {
          margin-top: 20px;
          display: flex;
          justify-content: flex-end;
        }
      }

      .section-tab {
        background: rgba(0, 0, 0, 0.1);
        height: 36px;
        border-radius: 4px;
        display: flex;
        width: 100%;
        padding: 4px;
        margin-bottom: 20px;

        > div {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 50%;
          border-radius: 4px;
          cursor: pointer;

          &.active {
            background: #fff;
          }

          span {
            font-weight: 500;
            font-size: 12px;
          }
        }
      }

      .ex-drawer-section {
        padding: 12px 15px;
        border-radius: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: transparent;
        border: 1px solid #E4E4E4;
        cursor: pointer;

        &.active {
          background: rgba(119, 169, 100, 0.2);
          border-color: #77a964;

          .label p {
            color: #77a964;
            font-weight: 500;
          }

          .value {
            opacity: 1;
          }
        }

        &:not(:last-child) {
          margin-bottom: 12px;
        }

        .label {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          max-width: 65%;

          p {
            font-size: 13px;
            font-weight: 400;
            color: #000000;
            margin-bottom: 5px;
          }

          span {
            font-size: 10.5px;
            line-height: 1.6;
          }
        }

        .value {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          opacity: 0.5;

          svg {
            height: 10px;
            width: auto;
            margin-bottom: 15px;
          }

          h5 {
            margin: 0;
            font-weight: 500;
            font-size: 0.875rem;
          }
        }
      }
    }

    &--footer {
      position: absolute;
      right: 0;
      bottom: 0;
      height: 70px;
      width: 100%;
      background: #fff;
      transition: height 0.3s ease-in;
      box-shadow: 0px -2px 5px rgba(0, 0, 0, 0.1);
      z-index: 99;

      &.open {
        border-top: 5px solid #4caf50;
        height: 72%;
        transition: height 0.3s ease-out;
      }

      .summary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px;

        h4, h5 {
          margin: 0;
          line-height: 1;
        }

        h5 {
          font-weight: 500;
          font-size: 14px;
        }

        h4 {
          font-weight: 600;
          font-size: 18px;
        }

        span {
          text-decoration: underline;
          color: #4caf50;
          font-size: 11px;
          font-weight: 500;
          line-height: 1;
          cursor: pointer;
        }
      }

      .breakdown {
        padding: 15px;

        > div {
          display: flex;
          justify-content: space-between;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          padding: 5px 0;

          p {
            font-size: 0.75rem;
          }

          span {
            font-size: 0.75rem;
          }
        }

        .close {
          display: flex;
          justify-content: center;
          margin:  15px 0 0;
          text-decoration: underline;
          color: #4caf50;
          font-size: 11px;
          font-weight: 500;
          line-height: 1;
          cursor: pointer;
        }
      }
    }
  }
}

@-webkit-keyframes blink {
  0% {opacity: 1;}
  50%{opacity:1;}
  100%{opacity:0;}
}
@-moz-keyframes blink {
  0% {opacity: 1;}
  50%{opacity:1;}
  100%{opacity:0;}
}
@keyframes blink {
  0% {opacity: 1;}
  50%{opacity:1;}
  100%{opacity:0;}
}
.ex-drawer--trigger:hover {
  background: #4caf50;
  -webkit-animation: none;
  -moz-animation: none;
  animation: none;
}

@media (max-width: 600px) {
  .ex-drawer--wrapper {
    .ex-drawer {
      width: 90%;
      right: -100%;
    }
  }
}

.upload-file {
  flex-direction: column;
}

.upload-file label {
  display: flex;
  font-size: 11px;
  opacity: 1;
  position: static;
}

.upload-file label a {
  margin-left: auto;
}

.upload-file input {
  padding: 7px 0;
}

.left-margin {
  margin-left: 30px;
}

.not-visible {
  display: none;
}

.lower-part h4 {
  display: block;
  width: 100%;
}

.download-paye-template-link {
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
}
.asmnt-label {
  padding: 5px 0;
}

.tax-due {
  color: #43a047;
}

.disclaimer-note {
  padding: 6px;
  background: #FDA5A5;
  font-weight: 400;
}

.disclaimer-wrapper {
  font-size: 12px;
}
.disclaimer-wrapper .see-more-lnk {
  font-weight: 500;
  color: blue;
  cursor: pointer;
}
.disclaimer-wrapper p.dis-title {
  font-weight: 500;
}
</style>
